// @givmo/app-charity — claim-flow vocabulary (CPA-WEB2).
//
// The claim state machine's own translated, donor-honest vocabulary — the display copy + Badge tones
// for a claim's `status`, the submit `outcome` discriminator, and the Tier-2 verification document
// types. The backend already ships vocabulary-clean values (never internal grant terms — plan §3.6
// vocab quarantine); this module maps those wire strings to the plain-language labels the charity
// admin sees, defensively falling back so an unknown future value renders as a neutral, honest string
// rather than breaking the screen.
//
// Wire values (from the generated `CharityClaimRequestOut.status`, `CharityClaimSubmitResultOut.outcome`
// and `CharityVerificationDocumentType`) are treated as OPAQUE STRINGS — we switch on the known set and
// fall through for anything else (tolerant reader).

import type {
  CharityClaimRequestOut,
  CharitySignerRole,
  CharityVerificationDocumentType,
} from '@givmo/api-client';

// ── Submit outcomes (CharityClaimSubmitResultOut.outcome) ─────────────────────────────────────────
// The three terminal shapes of a submit call. 200 in all cases — a Tier-2 route and a cannot-claim
// dead-end are valid outcomes, NOT errors.
export const CLAIM_OUTCOME = {
  autoVerified: 'AUTO_VERIFIED',
  pendingReview: 'PENDING_REVIEW',
  cannotClaim: 'CANNOT_CLAIM',
} as const;

// ── Claim statuses (CharityClaimRequestOut.status) ────────────────────────────────────────────────
export const CLAIM_STATUS = {
  submitted: 'SUBMITTED',
  autoVerified: 'AUTO_VERIFIED',
  pendingReview: 'PENDING_REVIEW',
  approved: 'APPROVED',
  rejected: 'REJECTED',
} as const;

// ── Verification tiers (CharityClaimRequestOut.tier) ──────────────────────────────────────────────
export const CLAIM_TIER = {
  tier1: 'TIER_1',
  tier2: 'TIER_2',
} as const;

/** One operational source for every charity-facing manual-review estimate. */
export const CLAIM_REVIEW_SLA_COPY = '1–3 business days';

/** SaaS Badge tones (subset used by the claim UI). */
export type ClaimBadgeTone = 'success' | 'info' | 'warning' | 'danger' | 'neutral' | 'brand';

/** A claim status is terminal-approved (the claimant is now the Owner). */
export function isApprovedStatus(status: string): boolean {
  return status === CLAIM_STATUS.approved || status === CLAIM_STATUS.autoVerified;
}

/** A claim status is still awaiting a super-admin decision. */
export function isPendingStatus(status: string): boolean {
  return status === CLAIM_STATUS.pendingReview || status === CLAIM_STATUS.submitted;
}

/** A claim status was rejected by review. */
export function isRejectedStatus(status: string): boolean {
  return status === CLAIM_STATUS.rejected;
}

/** Plain-language label for a claim `status` (donor-honest; never internal vocabulary). */
export function claimStatusLabel(status: string): string {
  switch (status) {
    case CLAIM_STATUS.submitted:
      return 'Submitted';
    case CLAIM_STATUS.autoVerified:
    case CLAIM_STATUS.approved:
      return 'Approved';
    case CLAIM_STATUS.pendingReview:
      return 'Pending review';
    case CLAIM_STATUS.rejected:
      return 'Rejected';
    default:
      // Tolerant reader: an unknown future status renders as a neutral, humanized string.
      return humanizeToken(status);
  }
}

/** Badge tone for a claim `status`. */
export function claimStatusTone(status: string): ClaimBadgeTone {
  if (isApprovedStatus(status)) return 'success';
  if (isRejectedStatus(status)) return 'danger';
  if (isPendingStatus(status)) return 'info';
  return 'neutral';
}

/** Short, plain-language explanation of what a status means for the claimant. */
export function claimStatusDescription(status: string): string {
  switch (status) {
    case CLAIM_STATUS.submitted:
      return 'Your claim has been received.';
    case CLAIM_STATUS.autoVerified:
    case CLAIM_STATUS.approved:
      return 'Your claim was approved. You now manage this organization.';
    case CLAIM_STATUS.pendingReview:
      return `Our team is reviewing your claim. This usually takes ${CLAIM_REVIEW_SLA_COPY}.`;
    case CLAIM_STATUS.rejected:
      return 'This claim was not approved. See the reason below or contact support.';
    default:
      return 'Your claim is being processed.';
  }
}

// ── Verification document types (Tier-2) ──────────────────────────────────────────────────────────
export interface DocTypeMeta {
  /** The wire enum value posted to the backend. */
  value: CharityVerificationDocumentType;
  /** Short label. */
  label: string;
  /** What the reviewer needs. */
  description: string;
  /** Whether this doc is required for a standard Tier-2 review. */
  required: boolean;
}

/**
 * The document types a Tier-2 claim OFFERS. ONE is required — the IRS determination / EIN letter.
 * The fiscal-sponsor MOU + "other" are optional additions for the sponsor/edge cases.
 *
 * The officer-authorization letter was a second required upload until 2026-08-29 (see
 * `RETIRED_DOC_LABELS`). `CharityVerificationDocumentType` still fixes four wire values; this list
 * is what the portal ASKS for, which is deliberately narrower.
 */
export const DOC_TYPES: readonly DocTypeMeta[] = [
  {
    value: 'IRS_DETERMINATION_LETTER',
    label: 'IRS determination letter',
    description: 'Your 501(c)(3) determination / EIN letter from the IRS.',
    required: true,
  },
  {
    value: 'FISCAL_SPONSOR_MOU',
    label: 'Fiscal-sponsor MOU',
    description: 'If a fiscal sponsor manages this project, upload the sponsorship agreement.',
    required: false,
  },
  {
    value: 'OTHER',
    label: 'Other supporting document',
    description: 'Any additional document that supports your claim.',
    required: false,
  },
];

/**
 * Doc types the portal no longer OFFERS but must still name, because rows carrying them exist.
 *
 * `OFFICER_AUTHORIZATION` was a second required upload until 2026-08-29, when it was dropped:
 * competitor portals do not ask for one, and it was a real bottleneck for a charity admin who has
 * to get a letter signed before they can finish.
 *
 * The wire value is untouched, so documents uploaded under it still exist. This map is what the
 * CHARITY portal resolves them through — it renders SLOTS, so without a retired-document row (and
 * a label for it) a document whose slot is gone would simply disappear from the claimant's view.
 * The super-admin reviewer has its own independent label map (`charity-claims-decode.ts`, where the
 * string is "Officer authorization", without "letter") and has always rendered from the document
 * list, so it needed no change.
 *
 * A charity that wants to volunteer one can still use "Other supporting document".
 *
 * ⚠ The wire value lives in a native PostgreSQL ENUM type (`charityverificationdocumenttype`), not
 * a CHECK constraint — Postgres cannot drop a value from an enum at all, so retiring it for real
 * would mean a new type, a column migration and a drop. That buys nothing; leave it.
 */
const RETIRED_DOC_LABELS: Readonly<Record<string, string>> = {
  OFFICER_AUTHORIZATION: 'Officer authorization letter',
};

/** Label for a document-type wire value (tolerant of unknown + retired values). */
export function docTypeLabel(value: string): string {
  const offered = DOC_TYPES.find((d) => d.value === value)?.label;
  if (offered !== undefined) return offered;
  // `Object.hasOwn`, not a bare index: `RETIRED_DOC_LABELS['constructor']` would otherwise resolve
  // up the prototype chain and return a function typed as string. Unreachable today (the value is
  // backend-enum-constrained) but free to close.
  if (Object.hasOwn(RETIRED_DOC_LABELS, value)) return RETIRED_DOC_LABELS[value]!;
  return humanizeToken(value);
}

// ── Verification-document status ──────────────────────────────────────────────────────────────────
//
// The wire enum is `PENDING_UPLOAD | UPLOADED` and ONLY those two (`givmo-backend-python`
// origin/development `app/domain/value_objects.py:2590-2605`). Its own docstring says why there is no
// third: a reviewer approves the REQUEST (`CharityClaimRequestStatus`), never an individual document.
//
// These two used to branch on `ACCEPTED` / `APPROVED` / `REJECTED` as well — values the backend has
// never sent, which made a rejected-looking document badge unreachable rather than defensive. Removed
// rather than kept "just in case": the tolerant `default` already renders any future value honestly,
// and a per-document approve/reject vocabulary would be a backend decision to make first.

/** Plain-language label for a verification-document status. */
export function docStatusLabel(status: string): string {
  switch (status) {
    case 'UPLOADED':
      return 'Uploaded';
    case 'PENDING_UPLOAD':
      return 'Not uploaded yet';
    default:
      return humanizeToken(status);
  }
}

/**
 * Badge tone for a verification-document status.
 *
 * `UPLOADED` stays 'info', NOT 'success' — the document is in, but nobody has judged it. Green would
 * imply an approval that has not happened, and the approval that matters is of the request.
 */
export function docStatusTone(status: string): ClaimBadgeTone {
  return status === 'PENDING_UPLOAD' ? 'neutral' : 'info';
}

/** Turn a SCREAMING_SNAKE_CASE wire token into a human-readable label ("PENDING_REVIEW" → "Pending review"). */
export function humanizeToken(token: string): string {
  const cleaned = token.replace(/[_-]+/g, ' ').trim().toLowerCase();
  if (cleaned.length === 0) return token;
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

// ── Why a claim is in review ──────────────────────────────────────────────────────────────────────

/**
 * The codes the backend's instant-check engine records on a claim it sends to review: the checks
 * that failed, comma-joined, in the claim's `reason` (`givmo-backend-python`
 * `app/domain/charity_claim_verification.py`, a closed, stored vocabulary). Since D-CP-92 (be#3405)
 * instant verification runs only for a charity a super-admin has allowed, so a claimant whose email
 * IS at the charity's website still lands here, with `instant_claim_not_allowed` alone.
 */
const ENGINE_REVIEW_REASON = {
  emailNotVerified: 'email_not_verified',
  domainMismatch: 'domain_mismatch',
  personalOrUnreadableDomain: 'blacklisted_or_unparseable_domain',
  charityNotActive: 'charity_not_active',
  instantClaimNotAllowed: 'instant_claim_not_allowed',
  instantClaimAllowanceLapsed: 'instant_claim_allowance_lapsed',
  alreadyClaimed: 'already_claimed_takeover',
} as const;

const ENGINE_REVIEW_REASONS: ReadonlySet<string> = new Set(Object.values(ENGINE_REVIEW_REASON));

/**
 * The review note's three sentences, keyed by the claim's reason in {@link claimReviewNote}. A reviewer
 * CHECKS the documents and may refuse the claim, so no sentence says one will verify the claimant.
 */
const CLAIM_REVIEW_NOTE = {
  /** The email's domain is not the charity's website, on a charity whose claims can be instant. */
  domainMismatch:
    'Your email domain didn’t match this organization, so a reviewer checks your documents by hand ' +
    'instead of verifying you instantly.',
  /** No super-admin has allowed instant claims for this charity (or its website moved since). */
  checkedByHand: 'We check new claims by hand for now, so a reviewer will check your documents.',
  /** Any other reason, or one this app cannot read: true of every claim a person reviews. */
  neutral: 'A reviewer will check your documents.',
} as const;

/**
 * The engine's reasons on a claim, or null when its `reason` holds anything the engine does not
 * write — a code from before or after this app, a seed's marker, the reviewer's own words once the
 * claim is decided. Read token by token, all or nothing, the way the backend reads the same field
 * back (`email_verified_when_submitted`).
 */
function engineReviewReasons(reason: string | null | undefined): ReadonlySet<string> | null {
  if (typeof reason !== 'string') return null;
  const tokens = reason
    .split(',')
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
  if (tokens.length === 0 || !tokens.every((token) => ENGINE_REVIEW_REASONS.has(token))) {
    return null;
  }
  return new Set(tokens);
}

/**
 * Why THIS claim needs a person to look at it, in the claimant's own terms — or null when there is
 * nothing extra to explain.
 *
 * `TIER_2` is the internal name for the manual path, and a charity admin has never heard of a tier.
 * What they need is the true reason a person is reading their claim, and the tier alone cannot say
 * it (CPA-WEB91): the note keys on the claim's `reason`, and names the cause that sent it to review
 * whatever else was true —
 *   - instant claims not allowed for this charity (or allowed for a website it no longer has): every
 *     claim on it goes to a person, so that is the why, even when the email didn't match too, and
 *     the note never implies the claimant's email was wrong;
 *   - otherwise a domain mismatch, unless it is a takeover (never instant, so "instead of verifying
 *     you instantly" would be untrue): the one case where the instant check really was theirs to pass;
 *   - anything else, a takeover or a reason this app cannot read: the neutral sentence.
 * It deliberately does not assert that documents have already been provided — a Tier-2 claim can
 * still be waiting on its uploads, which is exactly why the surfaces carrying this note link to the
 * claim's own upload screen.
 */
export function claimReviewNote(
  claim: Pick<CharityClaimRequestOut, 'tier' | 'reason'>,
): string | null {
  if (claim.tier !== CLAIM_TIER.tier2) return null;
  const reasons = engineReviewReasons(claim.reason);
  if (reasons === null) return CLAIM_REVIEW_NOTE.neutral;
  if (
    reasons.has(ENGINE_REVIEW_REASON.instantClaimNotAllowed) ||
    reasons.has(ENGINE_REVIEW_REASON.instantClaimAllowanceLapsed)
  ) {
    return CLAIM_REVIEW_NOTE.checkedByHand;
  }
  if (
    reasons.has(ENGINE_REVIEW_REASON.domainMismatch) &&
    !reasons.has(ENGINE_REVIEW_REASON.alreadyClaimed)
  ) {
    return CLAIM_REVIEW_NOTE.domainMismatch;
  }
  return CLAIM_REVIEW_NOTE.neutral;
}

// ── Signer role (CPA-WEB12 · the bundled acceptance step) ─────────────────────────────────────────

/**
 * The statuses §12599.9(f)(1) enumerates — today exactly the four values
 * `SubmitClaimRequest.signer_role` accepts (generated `CharitySignerRole`; an unknown value is a 422)
 * — each with the label a person picking one would read, in the statute's own order.
 *
 * ⚠ THE VALUES ARE THE WIRE'S, NOT OURS, and they are a CLOSED set — not a "known values plus a
 * tolerant fallback" like every status enum above it. The difference is which direction the value
 * travels: a status is something the backend TELLS us, so an unknown one must render as a neutral
 * string rather than break a screen. A signer role is something we SEND, and it lands in a §318(a)
 * acceptance record as the represented authority of a specific human. There is no honest extra option
 * to offer, and "Other" is not a status the statute recognizes.
 *
 * ★ KEYED BY THE GENERATED UNION, WHICH IS THE WHOLE POINT: a backend that adds a fifth status fails
 * `typecheck` HERE, so the picker can never silently offer three quarters of the statutory set and
 * push a claimant into naming an authority they do not hold.
 *
 * ⚠ It was previously a tuple written `as const satisfies readonly CharitySignerRole[]`, which READS
 * like that guarantee and is not one: `satisfies` is a SUBSET assertion, so a fifth union member
 * passed it unnoticed (verified by mutation — adding `'incorporator'` to `CharitySignerRole` left
 * `typecheck` fully green). A guard that looks like exhaustiveness and is not is worse than no guard,
 * because it stops the next person looking. Do not go back to `satisfies` here.
 *
 * The labels are plainly capitalized rather than title-cased jargon, and deliberately NOT explained:
 * these are the statute's own words, and a gloss written here would be this app inventing a legal
 * definition of "officer" — the substance of what each status means lives in the agreement being
 * accepted, which is on screen directly above the picker.
 */
const SIGNER_ROLE_LABELS: Record<CharitySignerRole, string> = {
  officer: 'Officer',
  director: 'Director',
  trustee: 'Trustee',
  authorized_representative: 'Authorized representative',
};

export type SignerRole = CharitySignerRole;

/**
 * The closed set, derived from the labels map so the two can never diverge — a value with no label
 * cannot be offered, and a label with no value cannot exist. Order is the map's source order, which
 * is the statute's.
 */
export const SIGNER_ROLES = Object.keys(SIGNER_ROLE_LABELS) as readonly SignerRole[];

/** The picker's options, in the statute's own order. */
export const SIGNER_ROLE_OPTIONS: readonly { value: SignerRole; label: string }[] =
  SIGNER_ROLES.map((value) => ({ value, label: SIGNER_ROLE_LABELS[value] }));
